// Helpers PUROS (sem React/DOM) compartilhados pelo decorador V2. Concentram aqui as
// regras de fotos que precisam bater EXATAMENTE entre o host (DecorarImovel), as
// galerias (badge/botão), a seção de ideias e o TeaserModal — assim o "par real"
// (foto original ↔ foto decorada aprovada) é resolvido do mesmo jeito em todo lugar.
//
// Convenção do backend (fotos aprovadas de decoração):
//   descricao = "Ambiente decorado com IA — estilo X"  e, quando pareada,
//   sufixo " [antes:<fotoId>]" apontando pro id da foto ORIGINAL correspondente.

// id do slot na página do imóvel onde o host portaliza a seção "Ideias de decoração".
export const SECAO_SLOT_ID = 'decorador-ideias-slot';

// Ambientes que "vendem" a decoração — usados pra escolher a foto do teaser com
// inteligência (evita mandar fachada/planta pro gerador).
export const SMART_PHOTO_RE = /sala|living|estar|jantar|quarto|dormit|cozinha|kitchen|suite|su[íi]te/i;

export interface IFotoLite {
    url: string;
    label?: string;
    decoravel?: boolean;
    id?: number;
}

export interface IParReal {
    antesUrl: string;
    depoisUrl: string;
    estiloNome: string;
}

// Melhor URL GRANDE de UMA foto (mesma ordem de preferência das galerias, pras URLs
// baterem no dedupe do modal).
export const melhorUrlFoto = (foto: any): string =>
    String(foto?.url_foto || foto?.url_spaces_cdn || foto?.url_spaces_migracao || foto?.url_marca || '');

// Uma foto foi marcada como decorável no CRM (tbl_imovel_fotos.Decoravel → Foto.decoravel)?
export const fotoMarcadaDecoravel = (foto: any): boolean =>
    foto?.decoravel === true || Number(foto?.decoravel) === 1;

// O imóvel tem ALGUMA foto marcada como decorável? Decide o modo (curadoria x fallback).
export const temFotoMarcadaDecoravel = (detailsProperties: any): boolean =>
    (Array.isArray(detailsProperties?.fotos) ? detailsProperties.fotos : []).some(fotoMarcadaDecoravel);

// Fotos DECORÁVEIS do imóvel em URL grande, na ordem visual da galeria (posição,
// destaque primeiro), sem duplicadas. label = descrição (tooltip/uso do teaser).
// Curadoria: se o imóvel tem QUALQUER foto marcada (Decoravel=1), só as marcadas
// entram; senão (nenhuma marcada) fallback = TODAS (comportamento legado, pra não
// deixar imóvel sem a função até o CRM ser preenchido).
export const getFotosDecoraveis = (detailsProperties: any): IFotoLite[] => {
    const fotos = Array.isArray(detailsProperties?.fotos) ? [...detailsProperties.fotos] : [];
    if (!fotos.length) return [];
    fotos.sort((a: any, b: any) => Number(a?.posicao || 0) - Number(b?.posicao || 0));
    const idxDestaque = fotos.findIndex((f: any) => Number(f?.destaque) === 1);
    if (idxDestaque > 0) fotos.unshift(fotos.splice(idxDestaque, 1)[0]);
    const temMarcada = fotos.some(fotoMarcadaDecoravel);
    const vistas = new Set<string>();
    const lista: IFotoLite[] = [];
    fotos.forEach((f: any) => {
        const marcada = fotoMarcadaDecoravel(f);
        if (temMarcada && !marcada) return; // curadoria: só as marcadas
        const url = melhorUrlFoto(f);
        if (!url || vistas.has(url)) return;
        vistas.add(url);
        lista.push({
            url,
            label: f?.descricao ? String(f.descricao) : '',
            decoravel: marcada,
            id: f?.id != null ? Number(f.id) : undefined,
        });
    });
    return lista;
};

// Set de URLs decoráveis do imóvel (aplica whitelist+fallback UMA vez) — as galerias
// usam pra decidir o pill por foto sem reprocessar a lista a cada foto.
export const urlsDecoraveisSet = (detailsProperties: any): Set<string> =>
    new Set(getFotosDecoraveis(detailsProperties).map((f) => f.url));

// Esta foto pode ser decorada? Curadoria: se o imóvel tem QUALQUER foto marcada, só as
// marcadas; senão (nenhuma marcada) fallback = todas (comportamento legado).
export const podeDecorarFoto = (foto: any, detailsProperties: any): boolean =>
    temFotoMarcadaDecoravel(detailsProperties) ? fotoMarcadaDecoravel(foto) : true;

// A foto é uma decoração gerada por IA? Preferimos o vínculo relacional
// (id_foto_origem, robusto) e caímos na legenda "decorado com IA" (legado).
export const isFotoDecorada = (foto: any): boolean =>
    (foto?.id_foto_origem != null && Number(foto.id_foto_origem) > 0) ||
    /decorado com ia/i.test(String(foto?.descricao || ''));

// Extrai o id da foto ORIGINAL do marcador "[antes:123]".
export const parseAntesId = (descricao: any): number | null => {
    const m = String(descricao || '').match(/\[antes:\s*(\d+)\s*\]/i);
    return m ? Number(m[1]) : null;
};

// Extrai o nome do estilo da legenda ("... estilo X [antes:..]" → "X").
export const parseEstiloNome = (descricao: any): string => {
    const m = String(descricao || '').match(/estilo\s+([^[]+)/i);
    return m ? m[1].trim().replace(/\s+/g, ' ') : '';
};

// Dada uma foto DECORADA com marcador [antes:id], resolve o par real (antes ↔ depois)
// procurando a foto original na lista completa. null se faltar marcador/original/URL.
export const resolveParReal = (fotoDecorada: any, fotos: any[]): IParReal | null => {
    // Prefere o vínculo relacional id_foto_origem; fallback pro marcador [antes:<id>] (legado).
    const antesId =
        fotoDecorada?.id_foto_origem != null && Number(fotoDecorada.id_foto_origem) > 0
            ? Number(fotoDecorada.id_foto_origem)
            : parseAntesId(fotoDecorada?.descricao);
    if (!antesId) return null;
    const lista = Array.isArray(fotos) ? fotos : [];
    const original = lista.find((f) => Number(f?.id) === antesId);
    if (!original) return null;
    const antesUrl = melhorUrlFoto(original);
    const depoisUrl = melhorUrlFoto(fotoDecorada);
    if (!antesUrl || !depoisUrl) return null;
    return { antesUrl, depoisUrl, estiloNome: parseEstiloNome(fotoDecorada?.descricao) };
};

// Primeiro par real do imóvel (qualquer foto decorada com marcador válido).
export const primeiroParReal = (fotos: any[]): IParReal | null => {
    const lista = Array.isArray(fotos) ? fotos : [];
    for (const f of lista) {
        if (!isFotoDecorada(f)) continue;
        const par = resolveParReal(f, lista);
        if (par) return par;
    }
    return null;
};

// Foto "com inteligência" pro teaser: 1ª cujo label case a regex de ambiente; fallback
// = 1ª foto (capa). Ignora fotos que já são decoração aprovada (queremos o original).
export const escolherFotoTeaser = (fotos: IFotoLite[]): string => {
    if (!fotos.length) return '';
    const candidatas = fotos.filter((f) => !/decorado com ia/i.test(String(f.label || '')));
    const base = candidatas.length ? candidatas : fotos;
    // Se o corretor marcou fotos (curadoria), respeita a 1ª marcada na ordem visual.
    const marcada = base.find((f) => f.decoravel === true);
    if (marcada) return marcada.url || '';
    // Senão, heurística: 1ª foto de ambiente; fallback = 1ª foto (capa).
    const match = base.find((f) => SMART_PHOTO_RE.test(String(f.label || '')));
    return (match || base[0] || fotos[0])?.url || '';
};

// Quantas fotos PUBLICADAS de decoração este imóvel tem (prova social).
export const contarDecoradasPublicadas = (fotos: any[]): number =>
    (Array.isArray(fotos) ? fotos : []).filter(isFotoDecorada).length;
